import React, { useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { FlaskConical, Plus, Trophy, Play, Pause, Trash2, X, BarChart3, MousePointerClick, Eye, ShoppingCart, Zap, TrendingUp, Target, ChevronRight, Code, Copy, Globe2 } from 'lucide-react';

const GOAL_INFO = {
  ctr: { label: 'Click-Through Rate', icon: MousePointerClick, color: '#CDFE65', desc: 'Optimize for highest CTR' },
  conversion: { label: 'Conversion Rate', icon: ShoppingCart, color: '#06B6D4', desc: 'Optimize for conversions' },
  engagement: { label: 'Engagement', icon: TrendingUp, color: '#7C3AED', desc: 'Optimize for clicks + conversions' },
};

export default function ABTestingPage() {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTest, setSelectedTest] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [generations, setGenerations] = useState([]);
  const [form, setForm] = useState({ name: '', generation_ids: [], goal: 'ctr', auto_winner_threshold: 0.95 });
  const [creating, setCreating] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [detailTab, setDetailTab] = useState('metrics');
  const [embedData, setEmbedData] = useState(null);
  const [attribution, setAttribution] = useState(null);

  const load = useCallback(async () => {
    try {
      const t = await api.listABTests(statusFilter || undefined);
      setTests(t);
    } catch {} finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const openCreate = async () => {
    try {
      const gens = await api.listGenerations({ status: 'completed', limit: 20 });
      setGenerations(gens);
      setForm({ name: '', generation_ids: [], goal: 'ctr', auto_winner_threshold: 0.95 });
      setShowCreate(true);
    } catch { toast.error('Failed to load generations'); }
  };

  const toggleGenSelection = (id) => {
    setForm(f => ({
      ...f,
      generation_ids: f.generation_ids.includes(id)
        ? f.generation_ids.filter(x => x !== id)
        : f.generation_ids.length < 4 ? [...f.generation_ids, id] : f.generation_ids
    }));
  };

  const handleCreate = async () => {
    if (!form.name.trim()) { toast.error('Enter a test name'); return; }
    if (form.generation_ids.length < 2) { toast.error('Select at least 2 variants'); return; }
    setCreating(true);
    try {
      const test = await api.createABTest(form);
      toast.success('A/B test created!');
      setShowCreate(false);
      setSelectedTest(test);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setCreating(false); }
  };

  const handleSimulate = async (testId) => {
    try {
      const result = await api.simulateABTraffic(testId, 200);
      toast.success('Traffic simulated!');
      setSelectedTest(result);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Simulation failed'); }
  };

  const loadEmbed = async (testId) => {
    try { setEmbedData(await api.getABEmbed(testId)); } catch { toast.error('Failed to load embed codes'); }
  };

  const loadAttribution = async (testId) => {
    try { setAttribution(await api.getABAttribution(testId)); } catch { toast.error('Failed to load attribution'); }
  };

  const handleSimulateAttribution = async (testId) => {
    try {
      await api.simulateAttribution(testId, 500);
      toast.success('Attribution events simulated!');
      await loadAttribution(testId);
      const updated = await api.getABTest(testId);
      setSelectedTest(updated);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success('Copied!')).catch(() => toast.error('Copy failed'));
  };

  const handlePickWinner = async (testId, genId) => {
    try {
      await api.setABWinner(testId, genId);
      toast.success('Winner selected!');
      const updated = await api.getABTest(testId);
      setSelectedTest(updated);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handlePause = async (testId) => {
    try {
      const res = await api.pauseABTest(testId);
      toast.success(res.message);
      const updated = await api.getABTest(testId);
      setSelectedTest(updated);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (testId) => {
    if (!window.confirm('Delete this A/B test?')) return;
    try {
      await api.deleteABTest(testId);
      toast.success('Test deleted');
      setSelectedTest(null);
      load();
    } catch { toast.error('Failed to delete'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#CDFE65] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="animate-fade-in-up opacity-0" data-testid="ab-testing-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-black tracking-tight" style={{ fontFamily: 'Chivo' }}>Smart A/B <span className="text-[#CDFE65]">Testing</span></h1>
          <p className="text-white/40 mt-1">Test variants, track performance, auto-select winners</p>
        </div>
        <button onClick={openCreate} className="btn-primary text-sm flex items-center gap-2" data-testid="create-ab-test-btn">
          <Plus className="w-4 h-4" /> New Test
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Test List */}
        <div className="lg:col-span-4 space-y-3" data-testid="test-list">
          <div className="flex gap-2 mb-3">
            {['', 'active', 'completed', 'paused'].map(s => (
              <button key={s} onClick={() => setStatusFilter(s)} className={`text-xs px-3 py-1.5 rounded-full font-bold transition-all ${statusFilter === s ? 'bg-[#CDFE65] text-black' : 'bg-white/5 text-white/40'}`} data-testid={`filter-${s || 'all'}`}>
                {s || 'All'}
              </button>
            ))}
          </div>
          {tests.length === 0 ? (
            <div className="bg-[#0F0F11] border border-white/5 rounded-xl p-8 text-center">
              <FlaskConical className="w-10 h-10 text-white/10 mx-auto mb-3" />
              <p className="text-sm text-white/40">No tests yet</p>
            </div>
          ) : tests.map(test => (
            <div key={test.id} onClick={() => setSelectedTest(test)} className={`bg-[#0F0F11] border rounded-xl p-4 cursor-pointer card-hover ${selectedTest?.id === test.id ? 'border-[#CDFE65]' : 'border-white/5'}`} data-testid={`test-card-${test.id}`}>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-bold text-sm truncate">{test.name}</h3>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${test.status === 'active' ? 'bg-green-500/10 text-green-400' : test.status === 'completed' ? 'bg-[#CDFE65]/10 text-[#CDFE65]' : 'bg-yellow-500/10 text-yellow-400'}`}>{test.status}</span>
              </div>
              <div className="flex items-center gap-3 text-xs text-white/30 font-mono">
                <span>{test.variants?.length || 0} variants</span>
                <span>{test.total_impressions || 0} imp</span>
                {test.winner_id && <Trophy className="w-3 h-3 text-[#CDFE65]" />}
              </div>
              <div className="flex gap-1 mt-2">
                {(test.variants || []).map(v => (
                  <div key={v.generation_id} className={`h-1.5 flex-1 rounded-full ${v.generation_id === test.winner_id ? 'bg-[#CDFE65]' : 'bg-white/10'}`} />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Test Detail */}
        <div className="lg:col-span-8" data-testid="test-detail">
          {selectedTest ? (
            <div className="space-y-6">
              {/* Header */}
              <div className="bg-[#0F0F11] border border-white/5 rounded-xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-xl font-bold">{selectedTest.name}</h2>
                    <div className="flex items-center gap-3 mt-1 text-xs text-white/30 font-mono">
                      <span className="flex items-center gap-1">{GOAL_INFO[selectedTest.goal]?.icon && <Target className="w-3 h-3" />} Goal: {selectedTest.goal}</span>
                      <span>Threshold: {(selectedTest.auto_winner_threshold * 100).toFixed(0)}%</span>
                      <span>{selectedTest.created_at?.slice(0, 10)}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {selectedTest.status === 'active' && (
                      <button onClick={() => handleSimulate(selectedTest.id)} className="btn-primary text-xs py-1.5 px-3 flex items-center gap-1.5" data-testid="simulate-btn">
                        <Zap className="w-3.5 h-3.5" /> Simulate Traffic
                      </button>
                    )}
                    {selectedTest.status !== 'completed' && (
                      <button onClick={() => handlePause(selectedTest.id)} className="btn-secondary text-xs py-1.5 px-3 flex items-center gap-1.5" data-testid="pause-btn">
                        {selectedTest.status === 'paused' ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
                        {selectedTest.status === 'paused' ? 'Resume' : 'Pause'}
                      </button>
                    )}
                    <button onClick={() => handleDelete(selectedTest.id)} className="text-white/20 hover:text-red-400 p-1.5" data-testid="delete-test-btn"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>

                {/* Summary Stats */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-white/[0.02] rounded-lg p-3 text-center">
                    <Eye className="w-4 h-4 mx-auto mb-1 text-white/30" />
                    <div className="text-lg font-black" style={{ fontFamily: 'Chivo' }}>{(selectedTest.total_impressions || 0).toLocaleString()}</div>
                    <p className="text-[10px] text-white/30 uppercase tracking-wider">Impressions</p>
                  </div>
                  <div className="bg-white/[0.02] rounded-lg p-3 text-center">
                    <MousePointerClick className="w-4 h-4 mx-auto mb-1 text-[#CDFE65]" />
                    <div className="text-lg font-black" style={{ fontFamily: 'Chivo' }}>{(selectedTest.total_clicks || 0).toLocaleString()}</div>
                    <p className="text-[10px] text-white/30 uppercase tracking-wider">Clicks</p>
                  </div>
                  <div className="bg-white/[0.02] rounded-lg p-3 text-center">
                    <ShoppingCart className="w-4 h-4 mx-auto mb-1 text-[#06B6D4]" />
                    <div className="text-lg font-black" style={{ fontFamily: 'Chivo' }}>{(selectedTest.total_conversions || 0).toLocaleString()}</div>
                    <p className="text-[10px] text-white/30 uppercase tracking-wider">Conversions</p>
                  </div>
                </div>

                {/* Winner Badge */}
                {selectedTest.winner_id && (
                  <div className="mt-4 p-3 bg-[#CDFE65]/5 border border-[#CDFE65]/20 rounded-lg flex items-center gap-3" data-testid="winner-badge">
                    <Trophy className="w-5 h-5 text-[#CDFE65]" />
                    <div>
                      <p className="text-sm font-bold text-[#CDFE65]">Winner Found!</p>
                      <p className="text-xs text-white/40">{selectedTest.winner_reason === 'auto' ? 'Automatically selected by statistical significance' : 'Manually selected'}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Detail Tabs */}
              <div className="flex gap-2">
                {[
                  { key: 'metrics', label: 'Variants', icon: BarChart3 },
                  { key: 'tracking', label: 'Tracking Pixels', icon: Code },
                  { key: 'attribution', label: 'Attribution', icon: Globe2 },
                ].map(t => (
                  <button key={t.key} onClick={() => {
                    setDetailTab(t.key);
                    if (t.key === 'tracking') loadEmbed(selectedTest.id);
                    if (t.key === 'attribution') loadAttribution(selectedTest.id);
                  }} className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold transition-all ${detailTab === t.key ? 'bg-[#CDFE65] text-black' : 'bg-white/5 text-white/40 hover:text-white'}`} data-testid={`detail-tab-${t.key}`}>
                    <t.icon className="w-3.5 h-3.5" /> {t.label}
                  </button>
                ))}
              </div>

              {/* Variants Tab */}
              {detailTab === 'metrics' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="variants-grid">
                {(selectedTest.variants || []).map((v, i) => {
                  const isWinner = v.generation_id === selectedTest.winner_id;
                  const maxCtr = Math.max(...(selectedTest.variants || []).map(x => x.ctr || 0), 0.01);
                  const maxCr = Math.max(...(selectedTest.variants || []).map(x => x.conversion_rate || 0), 0.01);
                  return (
                    <div key={v.generation_id} className={`bg-[#0F0F11] border rounded-xl overflow-hidden ${isWinner ? 'border-[#CDFE65] ring-1 ring-[#CDFE65]/20' : 'border-white/5'}`} data-testid={`variant-${v.generation_id}`}>
                      {/* Image */}
                      <div className="relative">
                        {v.output_url ? (
                          <img src={v.output_url} alt="" className="w-full aspect-video object-cover" loading="lazy" />
                        ) : (
                          <div className="w-full aspect-video bg-[#18181B]" />
                        )}
                        <div className="absolute top-2 left-2 bg-black/60 text-white text-xs font-bold px-2 py-0.5 rounded">
                          Variant {String.fromCharCode(65 + i)}
                        </div>
                        {isWinner && (
                          <div className="absolute top-2 right-2 bg-[#CDFE65] text-black text-xs font-bold px-2 py-0.5 rounded flex items-center gap-1">
                            <Trophy className="w-3 h-3" /> Winner
                          </div>
                        )}
                      </div>

                      {/* Metrics */}
                      <div className="p-4 space-y-3">
                        <div className="flex items-center justify-between text-xs font-mono text-white/30">
                          <span>{v.model_id}</span>
                          <span>{v.template_id}</span>
                        </div>

                        {/* CTR Bar */}
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-white/50">CTR</span>
                            <span className="text-sm font-bold text-[#CDFE65]">{v.ctr || 0}%</span>
                          </div>
                          <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                            <div className="h-full bg-[#CDFE65] rounded-full transition-all duration-700" style={{ width: `${maxCtr > 0 ? ((v.ctr || 0) / maxCtr * 100) : 0}%` }} />
                          </div>
                        </div>

                        {/* Conv Rate Bar */}
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-white/50">Conv Rate</span>
                            <span className="text-sm font-bold text-[#06B6D4]">{v.conversion_rate || 0}%</span>
                          </div>
                          <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                            <div className="h-full bg-[#06B6D4] rounded-full transition-all duration-700" style={{ width: `${maxCr > 0 ? ((v.conversion_rate || 0) / maxCr * 100) : 0}%` }} />
                          </div>
                        </div>

                        {/* Stats Row */}
                        <div className="grid grid-cols-3 gap-2 text-center text-[10px]">
                          <div><p className="text-white/20">Impressions</p><p className="font-bold">{(v.impressions || 0).toLocaleString()}</p></div>
                          <div><p className="text-white/20">Clicks</p><p className="font-bold">{(v.clicks || 0).toLocaleString()}</p></div>
                          <div><p className="text-white/20">Conversions</p><p className="font-bold">{(v.conversions || 0).toLocaleString()}</p></div>
                        </div>

                        {/* Pick Winner Button */}
                        {!selectedTest.winner_id && selectedTest.status === 'active' && (
                          <button onClick={() => handlePickWinner(selectedTest.id, v.generation_id)} className="w-full text-xs py-2 rounded-lg border border-white/10 text-white/40 hover:border-[#CDFE65] hover:text-[#CDFE65] transition-all" data-testid={`pick-winner-${v.generation_id}`}>
                            Pick as Winner
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              )}

              {/* Tracking Pixels Tab */}
              {detailTab === 'tracking' && (
                <div className="bg-[#0F0F11] border border-white/5 rounded-xl p-5 space-y-4" data-testid="tracking-tab">
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold flex items-center gap-2"><Code className="w-4 h-4 text-[#CDFE65]" /> Tracking Pixel Codes</h3>
                    <p className="text-xs text-white/30">Embed these in your ad creatives or landing pages</p>
                  </div>
                  {embedData ? (
                    <div className="space-y-4">
                      {embedData.embeds?.map((embed, i) => (
                        <div key={embed.generation_id} className="bg-white/[0.02] rounded-lg p-4 space-y-3">
                          <p className="text-sm font-bold">Variant {String.fromCharCode(65 + i)} <span className="text-xs text-white/30 font-mono ml-2">{embed.generation_id.slice(0, 8)}</span></p>
                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-[10px] uppercase tracking-wider text-white/40">Impression Pixel (HTML)</span>
                              <button onClick={() => copyToClipboard(embed.impression_pixel)} className="text-white/30 hover:text-[#CDFE65]" data-testid={`copy-impression-${i}`}><Copy className="w-3.5 h-3.5" /></button>
                            </div>
                            <pre className="text-[11px] font-mono bg-[#08080A] p-2 rounded text-[#CDFE65]/70 overflow-x-auto">{embed.impression_pixel}</pre>
                          </div>
                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-[10px] uppercase tracking-wider text-white/40">Click Tracking URL</span>
                              <button onClick={() => copyToClipboard(embed.click_pixel)} className="text-white/30 hover:text-[#CDFE65]"><Copy className="w-3.5 h-3.5" /></button>
                            </div>
                            <pre className="text-[11px] font-mono bg-[#08080A] p-2 rounded text-[#06B6D4]/70 overflow-x-auto">{embed.click_pixel}</pre>
                          </div>
                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-[10px] uppercase tracking-wider text-white/40">JavaScript Snippet</span>
                              <button onClick={() => copyToClipboard(embed.javascript_snippet)} className="text-white/30 hover:text-[#CDFE65]"><Copy className="w-3.5 h-3.5" /></button>
                            </div>
                            <pre className="text-[11px] font-mono bg-[#08080A] p-2 rounded text-[#7C3AED]/70 overflow-x-auto">{embed.javascript_snippet}</pre>
                          </div>
                          <div className="flex gap-2">
                            <span className="text-[9px] px-2 py-0.5 bg-blue-500/10 text-blue-400 rounded font-mono">Meta Ads</span>
                            <span className="text-[9px] px-2 py-0.5 bg-red-500/10 text-red-400 rounded font-mono">Google Ads</span>
                            <span className="text-[9px] px-2 py-0.5 bg-cyan-500/10 text-cyan-400 rounded font-mono">TikTok Ads</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8"><div className="w-6 h-6 border-2 border-[#CDFE65] border-t-transparent rounded-full animate-spin mx-auto" /></div>
                  )}
                </div>
              )}

              {/* Attribution Tab */}
              {detailTab === 'attribution' && (
                <div className="space-y-4" data-testid="attribution-tab">
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold flex items-center gap-2"><Globe2 className="w-4 h-4 text-[#7C3AED]" /> Multi-Touch Attribution</h3>
                    <button onClick={() => handleSimulateAttribution(selectedTest.id)} className="btn-primary text-xs py-1.5 px-3 flex items-center gap-1.5" data-testid="simulate-attribution-btn">
                      <Zap className="w-3.5 h-3.5" /> Simulate Events
                    </button>
                  </div>
                  {attribution ? (
                    <>
                      <div className="bg-[#0F0F11] border border-white/5 rounded-xl p-5">
                        <p className="text-xs font-mono uppercase tracking-wider text-white/30 mb-3">Event Summary — {(attribution.total_events || 0).toLocaleString()} total events</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {(attribution.attribution || []).map((a, i) => {
                            const channels = Object.entries(a.channels || {});
                            const totalChannelEvents = channels.reduce((sum, [, d]) => sum + (d.impressions || 0) + (d.clicks || 0) + (d.conversions || 0), 0);
                            return (
                              <div key={a.generation_id} className={`bg-white/[0.02] rounded-lg p-4 ${a.is_winner ? 'border border-[#CDFE65]/30' : 'border border-transparent'}`}>
                                <div className="flex items-center justify-between mb-3">
                                  <span className="text-sm font-bold">Variant {String.fromCharCode(65 + i)}</span>
                                  <div className="flex items-center gap-2 text-[10px] font-mono text-white/30">
                                    {a.is_winner && <Trophy className="w-3 h-3 text-[#CDFE65]" />}
                                    <span>{a.model_id}</span>
                                  </div>
                                </div>
                                <div className="grid grid-cols-3 gap-2 text-center text-[10px] mb-3">
                                  <div><p className="text-white/20">CTR</p><p className="font-bold text-[#CDFE65]">{a.metrics?.ctr || 0}%</p></div>
                                  <div><p className="text-white/20">Conv</p><p className="font-bold text-[#06B6D4]">{a.metrics?.conversion_rate || 0}%</p></div>
                                  <div><p className="text-white/20">Events</p><p className="font-bold">{a.total_events || 0}</p></div>
                                </div>
                                {/* Channel breakdown */}
                                <p className="text-[9px] uppercase tracking-wider text-white/20 mb-2">Channel Breakdown</p>
                                <div className="space-y-1.5">
                                  {channels.map(([channel, data]) => {
                                    const evts = (data.impressions || 0) + (data.clicks || 0) + (data.conversions || 0);
                                    const pct = totalChannelEvents > 0 ? Math.round(evts / totalChannelEvents * 100) : 0;
                                    const colors = { meta: '#1877F2', google: '#EA4335', tiktok: '#00F2EA', linkedin: '#0A66C2', direct: '#CDFE65', email: '#F59E0B' };
                                    return (
                                      <div key={channel} className="flex items-center gap-2">
                                        <span className="text-[10px] w-14 text-white/40 capitalize">{channel}</span>
                                        <div className="flex-1 h-1.5 bg-white/5 rounded-full overflow-hidden">
                                          <div className="h-full rounded-full" style={{ width: `${pct}%`, background: colors[channel] || '#888' }} />
                                        </div>
                                        <span className="text-[10px] text-white/30 w-8 text-right">{pct}%</span>
                                      </div>
                                    );
                                  })}
                                </div>
                                <div className="mt-2 flex gap-2 text-[9px] font-mono text-white/20">
                                  <span>First touch: {a.first_touch_source}</span>
                                  <span>Last touch: {a.last_touch_source}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="bg-[#0F0F11] border border-white/5 rounded-xl p-12 text-center">
                      <Globe2 className="w-10 h-10 text-white/5 mx-auto mb-3" />
                      <p className="text-sm text-white/30">Simulate attribution events to see channel breakdown</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="bg-[#0F0F11] border border-white/5 rounded-xl p-16 text-center">
              <FlaskConical className="w-12 h-12 text-white/5 mx-auto mb-4" />
              <p className="text-white/20">Select a test or create a new one</p>
            </div>
          )}
        </div>
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="create-ab-modal">
          <div className="bg-[#0F0F11] border border-white/10 rounded-2xl p-6 w-full max-w-lg mx-4 max-h-[85vh] overflow-y-auto animate-fade-in-up opacity-0">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">Create A/B Test</h2>
              <button onClick={() => setShowCreate(false)} className="text-white/30 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-5">
              <div>
                <label className="text-xs uppercase tracking-wider text-white/40 font-mono mb-1.5 block">Test Name</label>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="forge-input w-full" placeholder="Homepage Hero Banner Test" data-testid="test-name-input" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-white/40 font-mono mb-1.5 block">Goal</label>
                <div className="grid grid-cols-3 gap-2">
                  {Object.entries(GOAL_INFO).map(([key, info]) => (
                    <button key={key} onClick={() => setForm(f => ({ ...f, goal: key }))} className={`p-3 rounded-lg border text-center transition-all ${form.goal === key ? 'border-[#CDFE65] bg-[#CDFE65]/5' : 'border-white/5 hover:border-white/20'}`} data-testid={`goal-${key}`}>
                      <p className="text-xs font-bold">{info.label}</p>
                      <p className="text-[10px] text-white/30 mt-0.5">{info.desc}</p>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-white/40 font-mono mb-1.5 block">Confidence: {(form.auto_winner_threshold * 100).toFixed(0)}%</label>
                <input type="range" min="50" max="99" value={form.auto_winner_threshold * 100} onChange={e => setForm(f => ({ ...f, auto_winner_threshold: parseInt(e.target.value) / 100 }))} className="w-full" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-white/40 font-mono mb-2 block">Select Variants (2-4)</label>
                <div className="grid grid-cols-3 gap-2 max-h-[250px] overflow-y-auto">
                  {generations.filter(g => g.output_url).map(g => {
                    const sel = form.generation_ids.includes(g.id);
                    return (
                      <div key={g.id} onClick={() => toggleGenSelection(g.id)} className={`relative rounded-lg overflow-hidden border cursor-pointer transition-all ${sel ? 'border-[#CDFE65] ring-1 ring-[#CDFE65]/30' : 'border-white/5 hover:border-white/20'}`} data-testid={`select-gen-${g.id}`}>
                        <img src={g.output_url} alt="" className="w-full aspect-square object-cover" loading="lazy" />
                        {sel && <div className="absolute top-1 right-1 w-5 h-5 bg-[#CDFE65] rounded-full flex items-center justify-center text-black text-[10px] font-bold">{form.generation_ids.indexOf(g.id) + 1}</div>}
                        <div className="p-1.5 text-[9px] font-mono text-white/30 truncate">{g.template_id || g.model_id}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <button onClick={handleCreate} disabled={creating || form.generation_ids.length < 2} className="btn-primary w-full text-sm" data-testid="create-test-submit">
                {creating ? 'Creating...' : `Create Test (${form.generation_ids.length} variants)`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
